import React from 'react';
import { Box, Typography } from '@mui/material';
import { Star as StarIcon } from '@mui/icons-material';
import styled from 'styled-components';
import type { UrgentShiftRewardsSummary } from '../utils';
import shiftHeroBadge from 'images/shift-hero/shift-hero-badge.png';
import scrubCapIcon from 'images/shift-hero/scrub-cap.png';
import scrubTopIcon from 'images/shift-hero/scrub-top.png';

const Card = styled(Box)`
  background: #f7f8fb;
  border-radius: 16px;
  padding: 20px 16px 16px;
  margin-bottom: 24px;
  border: 1px solid #e6ebf2;
`;

const HeaderRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const StarBadge = styled(Box)`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #0b4598;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const IntroBox = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #e8f9f1;
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 16px;
`;

const UrgentPill = styled.span`
  display: inline-block;
  background: #fb7672;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 6px;
  line-height: 1.4;
  vertical-align: middle;
`;

const ProgressPill = styled(Box)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  column-gap: 10px;
  background: #0b4598;
  color: #fff;
  border-radius: 999px;
  padding: 8px 18px;
  margin: 0 auto 16px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
`;

const Gold = styled.span`
  color: #f5c542;
`;

const TrackWrap = styled(Box)`
  position: relative;
  height: 64px;
  margin: 8px 10px 22px;
`;

const TrackLine = styled(Box)<{ $pct: number }>`
  position: absolute;
  left: 0;
  right: 0;
  top: 18px;
  height: 6px;
  border-radius: 999px;
  background: #d9e0ea;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${(p) => Math.min(100, Math.max(0, p.$pct))}%;
    background: #0b8f9a;
  }
`;

type DotVariant = 'start' | 'badge' | 'cap' | 'star';

const MilestoneDot = styled(Box)<{
  $left: string;
  $variant: DotVariant;
  $reached?: boolean;
  $next?: boolean;
}>`
  position: absolute;
  left: ${(p) => p.$left};
  top: ${(p) => (p.$variant === 'start' ? '12px' : '4px')};
  transform: translateX(-50%);
  width: ${(p) => (p.$variant === 'start' ? '16px' : '34px')};
  height: ${(p) => (p.$variant === 'start' ? '16px' : '34px')};
  border-radius: 50%;
  display: grid;
  place-items: center;
  z-index: 1;
  box-sizing: border-box;
  line-height: 0;
  overflow: hidden;

  ${(p) =>
    p.$variant === 'start' &&
    `
    background: ${p.$reached ? '#0b8f9a' : '#ffffff'};
    border: 2px solid ${p.$reached ? '#0b8f9a' : '#c5d0e0'};
  `}

  ${(p) =>
    (p.$variant === 'badge' || p.$variant === 'cap') &&
    `
    background: ${p.$reached ? '#0b4598' : '#ffffff'};
    border: 2.5px solid ${p.$reached || p.$next ? '#f5c542' : '#c5d0e0'};
    box-shadow: ${p.$reached || p.$next ? '0 0 0 3px rgba(245, 197, 66, 0.28)' : 'none'};
  `}

  ${(p) =>
    p.$variant === 'star' &&
    `
    background: #f5c542;
    border: 2.5px solid #e8b52e;
    box-shadow: 0 0 0 4px rgba(245, 197, 66, 0.32);
  `}
`;

const Glyph = styled.span<{ $nudgeY?: number; $size?: number }>`
  display: grid;
  place-items: center;
  line-height: 0;
  font-size: ${(p) => p.$size || 16}px;
  transform: translateY(${(p) => p.$nudgeY || 0}px);

  > svg,
  > img {
    display: block;
  }
`;

const TrackIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

const MilestoneLabel = styled(Typography)<{ $left: string }>`
  position: absolute;
  left: ${(p) => p.$left};
  top: 44px;
  transform: translateX(-50%);
  font-size: 11px !important;
  font-weight: 600 !important;
  color: #374151;
  white-space: nowrap;
`;

const RewardGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 12px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const RewardCard = styled(Box)<{ $highlight?: boolean }>`
  background: ${(p) => (p.$highlight ? '#fff8e1' : '#fff')};
  border: 1.5px solid ${(p) => (p.$highlight ? '#f5c542' : '#e6ebf2')};
  border-radius: 12px;
  padding: 12px 10px;
  text-align: center;
  min-height: 108px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const RewardIcon = styled.img`
  width: 44px;
  height: 44px;
  object-fit: contain;
  margin: 6px 0 8px;
`;

const Footer = styled(Typography)`
  text-align: center;
  color: #6b7280;
  font-size: 13px !important;
`;

function progressPct(count: number): number {
  // Visual track: 0 → 3 → 5 → 10. Map count onto those anchors.
  if (count <= 0) return 0;
  if (count >= 10) return 100;
  if (count <= 3) return (count / 3) * 33.33;
  if (count <= 5) return 33.33 + ((count - 3) / 2) * 33.33;
  return 66.66 + ((count - 5) / 5) * 33.34;
}

/** T2-6995 resume / badge label (was Shift Saviour). */
export const SHIFT_HERO_LABEL =
  'Shift Hero - known for picking up URGENT shifts offices need filled.';

/** @deprecated Use SHIFT_HERO_LABEL — kept for any leftover imports during rebrand. */
export const SHIFT_SAVIOUR_LABEL = SHIFT_HERO_LABEL;

const UrgentShiftRewardsCard: React.FC<{
  rewards?: UrgentShiftRewardsSummary | null;
  id?: string;
}> = ({ rewards, id = 'urgent-shift-rewards-card' }) => {
  const count = rewards?.completedCount ?? 0;
  const reached = new Set(rewards?.reachedMilestones || []);
  const next = rewards?.nextMilestone;
  const footer =
    rewards?.footerMessage
    || (count >= 10
      ? 'Amazing job!! Keep picking up those URGENT shifts!'
      : `You're ${Math.max(0, (next || 3) - count)} shift${(next || 3) - count === 1 ? '' : 's'} away from your next reward 🎉`);

  const reached3 = reached.has(3) || count >= 3;
  const reached5 = reached.has(5) || count >= 5;
  const reached10 = reached.has(10) || count >= 10;

  return (
    <Card id={id} data-testid={id} aria-label="Short Notice Cancellations rewards">
      <HeaderRow>
        <StarBadge>
          <StarIcon sx={{ color: '#f5c542', fontSize: 22 }} />
        </StarBadge>
        <Typography variant="h6" fontWeight={700} color="#0B4598">
          Short Notice Cancellations
        </Typography>
      </HeaderRow>

      <IntroBox>
        <StarIcon sx={{ color: '#f5c542', fontSize: 18, mt: '2px' }} />
        <Typography variant="body2" color="#1f2937">
          Last-minute cancellations hurt offices and their patients. Be a hero by picking up{' '}
          <UrgentPill>URGENT</UrgentPill>
          {' '}shifts — get recognized, stand out, and earn rewards!
        </Typography>
      </IntroBox>

      <Box display="flex" justifyContent="center">
        <ProgressPill data-testid="urgent-shift-progress-pill">
          <span>Your progress:</span>
          <Gold>{`${count} / 10 shifts`}</Gold>
        </ProgressPill>
      </Box>

      <TrackWrap aria-hidden>
        <TrackLine $pct={progressPct(count)} />

        <MilestoneDot
          $left="0%"
          $variant="start"
          $reached={count > 0}
          data-testid="urgent-milestone-dot-0"
        />
        <MilestoneLabel $left="0%">0</MilestoneLabel>

        <MilestoneDot
          $left="33.33%"
          $variant="badge"
          $reached={reached3}
          $next={next === 3}
          data-testid="urgent-milestone-dot-3"
          aria-label="3 shifts Shift Hero badge"
        >
          <Glyph aria-hidden>
            <TrackIcon src={shiftHeroBadge} alt="" />
          </Glyph>
        </MilestoneDot>
        <MilestoneLabel $left="33.33%">3 shifts</MilestoneLabel>

        <MilestoneDot
          $left="66.66%"
          $variant="cap"
          $reached={reached5}
          $next={next === 5}
          data-testid="urgent-milestone-dot-5"
          aria-label="5 shifts scrub cap"
        >
          <Glyph aria-hidden>
            <TrackIcon src={scrubCapIcon} alt="" />
          </Glyph>
        </MilestoneDot>
        <MilestoneLabel $left="66.66%">5 shifts</MilestoneLabel>

        <MilestoneDot
          $left="100%"
          $variant="star"
          $reached={reached10}
          $next={next === 10 || count >= 10}
          data-testid="urgent-milestone-dot-10"
          aria-label="10 shifts star"
        >
          <Glyph>
            <StarIcon sx={{ color: '#ffffff', fontSize: 17 }} />
          </Glyph>
        </MilestoneDot>
        <MilestoneLabel $left="100%">10 shifts</MilestoneLabel>
      </TrackWrap>

      <RewardGrid>
        <RewardCard $highlight={next === 3 || (count >= 0 && count < 3)} data-testid="urgent-milestone-1-card">
          <Typography variant="caption" color="text.secondary" fontWeight={700}>MILESTONE 1</Typography>
          <RewardIcon src={shiftHeroBadge} alt="Shift Hero Badge" />
          <Typography variant="body2" fontWeight={700} color="#0B4598">Shift Hero Badge</Typography>
          <Typography variant="body2" fontWeight={700} color="#fb7672">+ $50 Gift Card</Typography>
        </RewardCard>
        <RewardCard $highlight={next === 5} data-testid="urgent-milestone-2-card">
          <Typography variant="caption" color="text.secondary" fontWeight={700}>MILESTONE 2</Typography>
          <RewardIcon src={scrubCapIcon} alt="TempStars Scrub Cap" />
          <Typography variant="body2" fontWeight={700} color="#0B4598">TempStars Scrub Cap</Typography>
        </RewardCard>
        <RewardCard $highlight={next === 10 || count >= 10} data-testid="urgent-top-tier-card">
          <Typography variant="caption" color="text.secondary" fontWeight={700}>TOP TIER</Typography>
          <RewardIcon src={scrubTopIcon} alt="TempStars Scrub Top" />
          <Typography variant="body2" fontWeight={700} color="#0B4598">TempStars Scrub Top</Typography>
        </RewardCard>
      </RewardGrid>

      <Footer data-testid="urgent-shift-footer">{footer}</Footer>
    </Card>
  );
};

export default UrgentShiftRewardsCard;
